import { Component, ComponentInterface, Prop, Watch } from "@stencil/core";
import api from "../../utils/api";
import serializeForm from "../../utils/serializeForm";
import waitForHydrated from "../../utils/waitForHydrated";
import {
    UserAdministrationItem,
    UserAdministrationResponse,
} from "./user-administration-response";
import row from "./row";

@Component({
    tag: "ux-iws-data-controller-user-administration",
    styleUrl: "data-controller-user-administration.scss",
})
export class DataControllerUserAdministration implements ComponentInterface {
    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** CSS selector for the filter */
    @Prop() filterSelector: string = "";
    /** CSS selector for the date form */
    @Prop() dateFormSelector: string = "";
    /** CSS selector for the sort form */
    @Prop() sortFormSelector: string = "";
    /** CSS selector for the table */
    @Prop() tableSelector: string = "";
    /** CSS selector for the element where the total number of order returns should be placed */
    @Prop() totalNumberSelector: string = "";
    /** CSS selector for the element where the number of filtered order returns should be placed */
    @Prop() filteredNumberSelector: string = "";
    /** CSS selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";

    /** element that contains all filters */
    private filter: HTMLUxFilterElement | null = null;
    /** form element for filter */
    private filterForm: HTMLFormElement | null = null;
    /** form element for date range */
    private dateForm: HTMLFormElement | null = null;
    /** form element for sorting */
    private sortForm: HTMLFormElement | null = null;
    /** table element */
    private table: HTMLTableElement | null = null;
    /** table body element where the data should be put */
    private tableBody: HTMLTableSectionElement | null = null;
    /** NodeList of DOM elements where the number of filtered order returns should be put */
    private filteredNumberElements: NodeList | null = null;
    /** NodeList of DOM elements where the total number of order returns should be put */
    private totalNumberElements: NodeList | null = null;
    /** URL for getting the next page */
    private nextPage: string = "";
    /** DOM element where all the table rows should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;

    componentDidLoad() {
        this.setSortForm();
        this.setFilter();
        this.setTable();
        this.setScrollSensor();
        this.setTotalNumberElements();
        this.setFilteredNumberElements();
        this.requestData(this.url);
    }

    /** Connect filter */
    @Watch("filterSelector")
    setFilter() {
        if (this.filterSelector === "") return;
        this.filter = document.querySelector(
            this.filterSelector
        ) as HTMLUxFilterElement;
        waitForHydrated(this.filter, () => this.setFilterForm());
        this.filter.addEventListener("uxFilterChange", () =>
            this.requestData(this.url)
        );
    }

    /** Connect sort form */
    @Watch("sortFormSelector")
    setSortForm() {
        if (this.sortFormSelector === "") return;
        this.sortForm = document.querySelector(
            this.sortFormSelector
        ) as HTMLFormElement;
        if (this.sortForm === null) return;
        this.sortForm.addEventListener("submit", preventDefaultEvent);
    }

    /** Connect total number */
    @Watch("totalNumberSelector")
    setTotalNumberElements() {
        if (this.totalNumberSelector === "") return;
        this.totalNumberElements = document.querySelectorAll(
            this.totalNumberSelector
        );
    }

    /** Connect filtered number */
    @Watch("filteredNumberSelector")
    setFilteredNumberElements() {
        if (this.filteredNumberSelector === "") return;
        this.filteredNumberElements = document.querySelectorAll(
            this.filteredNumberSelector
        );
    }

    private setFilterForm() {
        this.filterForm = this.filter.querySelector("form") as HTMLFormElement;
        if (this.filterForm === null) return;
        this.filterForm.addEventListener("submit", preventDefaultEvent);
    }

    private syncSortStateToTable() {
        if (this.table === null || this.sortForm === null) return;
        const { value } = this.sortForm.querySelector("input");
        const prevSorted = this.table.querySelector(
            `ux-table-sort[sorted-direction="desc"], ux-table-sort[sorted-direction="asc"]`
        ) as HTMLUxTableSortElement;
        if (prevSorted !== null) {
            prevSorted.sortedDirection = "";
        }
        const ascSort = this.table.querySelector(
            `ux-table-sort[asc-name="${value}"]`
        ) as HTMLUxTableSortElement;
        const descSort = this.table.querySelector(
            `ux-table-sort[desc-name="${value}"]`
        ) as HTMLUxTableSortElement;
        if (ascSort !== null) {
            ascSort.sortedDirection = "asc";
        }
        if (descSort !== null) {
            descSort.sortedDirection = "desc";
        }
    }

    private updateNumbers(filtered: number, total: number): void {
        writeToElements(this.filteredNumberElements, filtered.toString());
        writeToElements(this.totalNumberElements, total.toString());
    }

    /** connect table */
    @Watch("tableSelector")
    setTable() {
        if (this.tableSelector === "") return;
        this.table = document.querySelector(
            this.tableSelector
        ) as HTMLTableElement;
        this.tableBody = this.table?.querySelector("tbody");
        if (this.table !== null && this.sortForm !== null) {
            this.table.addEventListener("uxTableSort", (e: any) => {
                const value: string = e.detail;
                if (value === "") return;
                this.sortForm.querySelector("input").value = value;
                this.requestData(this.url);
            });
            this.syncSortStateToTable();
        }
    }

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = document.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestData(this.nextPage);
                }
            );
        }
    }

    /** post filter, date and sort settings to get data from the endpoint */
    @Watch("url")
    initialRequest() {
        this.requestData(this.url);
    }

    private getSerializedFormData() {
        return [
            serializeForm(this.dateForm),
            serializeForm(this.filterForm),
            serializeForm(this.sortForm),
        ]
            .filter((str) => str !== "")
            .join("&");
    }

    /** Send the real request and handle response */
    private requestData(url: string) {
        if (url === "") return;
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: this.getSerializedFormData(),
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };
        api<UserAdministrationResponse>(url, options).then((data) => {
            if (this.url === url) {
                this.clearExistingData();
                this.replaceFilter(data.filterHTML);
            }
            this.appendItemsToTable(data.items);
            this.updateNumbers(data.filteredCount, data.totalCount);
            this.nextPage = data.nextPage || "";
            if (this.nextPage !== "" && this.scrollSensor !== null) {
                this.scrollSensor.hasFired = false;
            }
        });
    }

    private replaceFilter(html: string | undefined) {
        if (
            typeof html === "string" &&
            html.length > 0 &&
            this.filter !== null
        ) {
            const parent = this.filter.parentElement;
            if (parent !== null) {
                parent.style.height = `${parent.offsetHeight}px`;
            }
            this.filter.outerHTML = html;
            this.setFilter();
            setTimeout(() => {
                parent.style.height = "auto";
            }, 200);
        }
    }

    private clearExistingData() {
        if (this.tableBody !== null) this.tableBody.textContent = "";
    }

    private appendItemsToTable(items: UserAdministrationItem[]) {
        if (this.tableBody === null) return;
        this.tableBody.insertAdjacentHTML("beforeend", items.map(row).join(""));
    }

    render() {
        return null;
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}

function preventDefaultEvent(e: Event) {
    e.preventDefault();
}

function writeToElements(nodes: NodeList | null, text: string): void {
    if (nodes === null) return;
    [].forEach.call(
        nodes,
        (element: HTMLElement) => (element.textContent = text)
    );
}
